import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { api, consulta } from '../api/cliente';
import { useSesion } from '../auth/sesion';
import type { Pagina } from '../api/cliente';
import {
  Aviso,
  Cargando,
  Encabezado,
  Etiqueta,
  Paginador,
  Tarjeta,
  Vacio,
  pesos,
} from '../ui/piezas';

interface ProyectoResumen {
  id: string;
  nombre: string;
  slug: string;
  constructora: string | null;
  ciudad: string;
  estado: string;
  estadoPublicacion: string;
  totalUnidades: number;
  unidadesDisponibles: number;
  precioDesde: number | null;
  precioHasta: number | null;
  areaDesde: number | null;
  areaHasta: number | null;
}

export default function Proyectos() {
  const { puede } = useSesion();
  const [pagina, setPagina] = useState(0);

  const listado = useQuery({
    queryKey: ['proyectos', pagina],
    queryFn: () =>
      api<Pagina<ProyectoResumen>>(`/admin/proyectos${consulta({ page: pagina, size: 24 })}`),
  });

  const filas = listado.data?.content ?? [];

  return (
    <>
      <Encabezado
        titulo="Proyectos"
        nota="Los rangos y los conteos los mantiene la base de datos; aquí solo se leen."
      >
        {puede('proyecto:crear') && <Link to="/proyectos/nuevo">Nuevo proyecto</Link>}
      </Encabezado>

      <Aviso error={listado.error} />

      <Tarjeta>
        {listado.isPending ? (
          <Cargando />
        ) : filas.length === 0 ? (
          <Vacio>Todavía no hay desarrollos cargados.</Vacio>
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Proyecto</th>
                  <th>Ciudad</th>
                  <th>Obra</th>
                  <th className="numero">Disponibles</th>
                  <th className="numero">Desde</th>
                  <th className="numero">Área</th>
                  <th>Publicación</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/proyectos/${p.id}`}>{p.nombre}</Link>
                      {p.constructora && (
                        <small style={{ opacity: 0.7, display: 'block' }}>{p.constructora}</small>
                      )}
                    </td>
                    <td>{p.ciudad}</td>
                    <td>{p.estado.replaceAll('_', ' ').toLowerCase()}</td>
                    <td className="numero">
                      {p.unidadesDisponibles} / {p.totalUnidades}
                    </td>
                    <td className="numero">{pesos(p.precioDesde)}</td>
                    <td className="numero">
                      {p.areaDesde == null
                        ? '—'
                        : `${p.areaDesde}${
                            p.areaHasta && p.areaHasta !== p.areaDesde ? `–${p.areaHasta}` : ''
                          } m²`}
                    </td>
                    <td>
                      <Etiqueta>{p.estadoPublicacion}</Etiqueta>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Paginador
          pagina={listado.data?.number ?? 0}
          total={listado.data?.totalElements ?? 0}
          paginas={listado.data?.totalPages ?? 0}
          alCambiar={setPagina}
        />
        <p className="campo__ayuda" style={{ marginTop: 10 }}>
          Cargar un proyecto y generar sus unidades todavía se hace por la API. Es lo siguiente de
          esta pantalla.
        </p>
      </Tarjeta>
    </>
  );
}
